import {
	type FieldMetadata,
	unstable_useControl as useControl,
	useInputControl,
} from '@conform-to/react'
import { type OTPInputProps, REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp'
import { LucideCirclePlus } from 'lucide-react'
import React, {
	type ComponentProps,
	type ElementRef,
	type ReactNode,
	useEffect,
	useId,
	useRef,
	useState,
} from 'react'
import { type DropzoneOptions } from 'react-dropzone'
import { Trans, useTranslation } from 'react-i18next'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '#app/components/ui/command.tsx'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.tsx'
import { IMAGES } from '#app/utils/config.js'
import { cn } from '#app/utils/misc.js'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from './ui/accordion.tsx'
import { Button } from './ui/button.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import {
	FileInput,
	FileUploader,
	FileUploaderContent,
	FileUploaderItem,
} from './ui/file-upload.tsx'
import { Icon } from './ui/icon.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp.tsx'
import {
	Input,
	type InputProps,
	PublicInput,
	PublicStyledInput,
} from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { InformationTooltip } from './ui/mollecules/tooltips.tsx'
import { RadioGroup, RadioGroupItem } from './ui/radio-group.tsx'
import RichTextInput from './ui/rich-text.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from './ui/select.tsx'
import { Switch } from './ui/switch.tsx'
import { PublicStyledTextarea, Textarea } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="h-fit space-y-1">
			{errorsToRender.map((e) => (
				<li
					key={e}
					className="h-fit text-[10px] leading-normal text-foreground-destructive"
				>
					{e}
				</li>
			))}
		</ul>
	)
}

// TODO: Translate this component
export function AllFormErrors({
	errors,
	className,
}: {
	errors: Record<string, string[]>
	className?: string
}) {
	const amountOfErrors = Object.keys(errors).length

	if (amountOfErrors === 0) return null

	return (
		<div
			className={cn(
				'w-full border border-red-800/70 bg-red-50/50 p-4 font-light',
				className,
			)}
		>
			<div className="flex">
				<div className="ml-3 flex-1">
					<h3 className="text-sm text-red-800/70">
						{amountOfErrors === 1
							? `There is an error with your submission`
							: `There are ${amountOfErrors} errors with your submission`}
					</h3>
					<div className="mt-2 text-sm text-red-800/70">
						<ul role="list" className="list-disc space-y-1 pl-5">
							{Object.entries(errors).map(([field, messages]) =>
								messages.map((message, i) => (
									<li key={i}>
										{process.env.NODE_ENV === 'development' && (
											<>
												<span className={'capitalize'}>
													{field.split('.').join(' ')}
												</span>
												:{' '}
											</>
										)}
										{message}
									</li>
								)),
							)}
						</ul>
					</div>
				</div>
			</div>
		</div>
	)
}

export function FieldSection({
	labelProps,
	hasRequiredFields = false,
	informationTooltip,
	children,
	accordionProps = {},
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	hasRequiredFields?: boolean
	informationTooltip?: {
		children: React.ReactNode
		align?: 'start' | 'end' | 'center'
	}
	children: React.ReactNode
	accordionProps?: {
		defaultOpen?: boolean
	}
}) {
	return (
		<Accordion
			type="single"
			defaultValue={accordionProps.defaultOpen ? 'item' : ''}
		>
			<AccordionItem value="item">
				<AccordionTrigger>
					<div className="flex items-center justify-between gap-xxs">
						<div className="flex flex-col items-start">
							<Label
								{...labelProps}
								className={cn(
									'flex-1 text-lg font-medium',
									labelProps.className,
								)}
							/>
							{hasRequiredFields && (
								<p className="ml-px text-xs text-amber-600">
									Contains required fields
								</p>
							)}
						</div>

						{informationTooltip && (
							<InformationTooltip align={informationTooltip.align ?? 'center'}>
								{informationTooltip.children}
							</InformationTooltip>
						)}
					</div>
				</AccordionTrigger>

				<AccordionContent className="flex flex-col gap-xxs px-1">
					{children}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}

export function PublicStyledField({
	placeholder,
	inputProps,
	helperProps,
	errors,
	className,
}: {
	placeholder: string
	inputProps: InputProps
	helperProps?: React.HTMLAttributes<HTMLDivElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const input = useInputControl({
		name: inputProps.name ?? '',
		formId: inputProps.form ?? '',
		initialValue: inputProps.defaultValue
			? String(inputProps.defaultValue)
			: undefined,
	})
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={cn('relative', className)}>
			<PublicStyledInput
				placeholder={placeholder}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
				onChange={(e) => {
					input.change(e.target.value)
				}}
				onBlur={input.blur}
				onFocus={input.focus}
			/>
			{helperProps ? (
				<div
					{...helperProps}
					className={cn(
						'mt-xxs text-xs font-light text-muted-foreground',
						helperProps.className,
					)}
				/>
			) : null}
			{errorId ? (
				<div className="absolute -bottom-sm font-light">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function PublicField({
	placeholder,
	inputProps,
	helperProps,
	errors,
	className,
}: {
	placeholder: string
	inputProps: InputProps
	helperProps?: React.HTMLAttributes<HTMLDivElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const input = useInputControl({
		name: inputProps.name ?? '',
		formId: inputProps.form ?? '',
		initialValue: inputProps.defaultValue
			? String(inputProps.defaultValue)
			: undefined,
	})
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<PublicInput
				placeholder={`${placeholder}${inputProps.required ? '*' : ''}`}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
				onChange={(e) => {
					input.change(e.target.value)
				}}
				onBlur={input.blur}
				onFocus={input.focus}
			/>
			{helperProps ? (
				<div
					{...helperProps}
					className={cn(
						'mt-xxs text-xs font-light text-muted-foreground',
						helperProps.className,
					)}
				/>
			) : null}
			{errorId ? (
				<div className="pt-2 font-light">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function Field({
	labelProps,
	inputProps,
	helperProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: InputProps
	helperProps?: React.HTMLAttributes<HTMLDivElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const input = useInputControl({
		name: inputProps.name ?? '',
		formId: inputProps.form ?? '',
		initialValue: inputProps.defaultValue
			? String(inputProps.defaultValue)
			: undefined,
	})
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			{labelProps.children && (
				<Label htmlFor={id} className="text-muted-foreground" {...labelProps}>
					{labelProps.children}
					{inputProps.required && (
						<span className="text-xs text-red-600"> *</span>
					)}
				</Label>
			)}
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
				onChange={(e) => {
					input.change(e.target.value)
				}}
				onBlur={input.blur}
				onFocus={input.focus}
			/>
			{helperProps && (
				<div
					{...helperProps}
					className={cn(
						'mt-xxs text-xs text-muted-foreground',
						helperProps.className,
					)}
				/>
			)}
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function NumberField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: InputProps
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const input = useInputControl({
		name: inputProps.name ?? '',
		formId: inputProps.form ?? '',
		initialValue: inputProps.defaultValue
			? String(inputProps.defaultValue)
			: undefined,
	})
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} className="text-muted-foreground" {...labelProps}>
				{labelProps.children}{' '}
				{inputProps.required && <span className="text-xs text-red-600">*</span>}
			</Label>
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				placeholder="Write a number"
				{...inputProps}
				suffix={
					<div>
						<Button
							onClick={() => {
								if ((Number(input.value) ?? 0) > 0)
									input.change(
										String((input.value ? Number(input.value) : 0) - 1),
									)
							}}
							className="text-lg"
							type="button"
							size="xs"
							variant="ghost"
						>
							-
						</Button>
						<Button
							onClick={() => {
								input.change(
									String((input.value ? Number(input.value) : 0) + 1),
								)
							}}
							className="text-lg"
							type="button"
							size="xs"
							variant="ghost"
						>
							+
						</Button>
					</div>
				}
			/>
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} className="text-muted-foreground" {...labelProps}>
				{labelProps.children}{' '}
				{inputProps.required && <span className="text-xs text-red-600">*</span>}
			</Label>
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function RadioGroupConform({
	meta,
	groupItemClassName,
	items,
}: {
	meta: FieldMetadata<string>
	groupItemClassName?: string
	items: Array<{
		value: string
		label: string | ReactNode
		disabled?: boolean
	}>
}) {
	const radioGroupRef = useRef<ElementRef<typeof RadioGroup>>(null)
	const control = useControl(meta)

	return (
		<>
			<input
				ref={control.register}
				name={meta.name}
				defaultValue={meta.initialValue}
				tabIndex={-1}
				className="sr-only"
				onFocus={() => {
					radioGroupRef.current?.focus()
				}}
			/>
			<RadioGroup
				ref={radioGroupRef}
				className="flex items-start gap-4"
				value={control.value ?? ''}
				onValueChange={control.change}
				onBlur={control.blur}
			>
				{items.map((item) => {
					return (
						<div className="flex items-center gap-2" key={item.value}>
							<RadioGroupItem
								disabled={item.disabled}
								value={item.value}
								className={groupItemClassName}
								id={`${meta.id}-${item.value}`}
							/>
							<label htmlFor={`${meta.id}-${item.value}`}>{item.label}</label>
						</div>
					)
				})}
			</RadioGroup>
		</>
	)
}

export function PublicStyledTextareaField({
	textareaProps,
	errors,
	className,
}: {
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={cn('relative', className)}>
			<PublicStyledTextarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			{errorId ? (
				<div className="absolute -bottom-sm font-light">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} className="text-muted-foreground" {...labelProps}>
				{labelProps.children}{' '}
				{textareaProps.required && (
					<span className="text-xs text-red-600">*</span>
				)}
			</Label>
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<Label
					htmlFor={id}
					{...labelProps}
					className={cn(
						'self-center text-body-xs font-light text-muted-foreground',
						labelProps.className,
					)}
				/>
			</div>
			{errorId ? (
				<div className="pt-1 font-light">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function SwitchField({
	labelProps,
	meta,
	className,
	defaultValue = false, // Provide a default value
}: {
	labelProps: JSX.IntrinsicElements['label']
	meta: FieldMetadata<boolean | 'true' | 'false' | null | undefined>
	className?: string
	defaultValue?: boolean
}) {
	const errors = meta.errors
	const fallbackId = useId()
	const switchRef = useRef<ElementRef<typeof Switch>>(null)
	const control = useControl(meta)
	const id = fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	useEffect(() => {
		if (control.value === undefined && defaultValue) {
			control.change(defaultValue ? 'true' : 'false')
		}
		if (control.value === 'on') {
			control.change('true')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div
			className={cn(
				'rounded-md border border-primary/50 bg-secondary/20 px-xxs py-[7px]',
				className,
			)}
		>
			<div className="flex w-full items-center justify-between gap-2">
				<Label
					htmlFor={id}
					className="self-center text-body-xs text-muted-foreground"
					{...labelProps}
				/>
				<input
					name={meta.name}
					ref={control.register}
					defaultValue={meta.initialValue}
					className="sr-only"
					id={id}
					tabIndex={-1}
					onFocus={() => {
						switchRef.current?.focus()
					}}
				/>
				<Switch
					ref={switchRef}
					checked={
						control.value === 'true' ||
						(control.value === undefined && defaultValue)
					}
					onCheckedChange={(checked) => {
						control.change(checked ? 'true' : 'false')
					}}
					onBlur={control.blur}
					className="focus:ring-2 focus:ring-stone-950 focus:ring-offset-2"
				></Switch>
			</div>
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function PublicStyledSelect({
	meta,
	placeholder,
	items,
	fieldErrors,
	className,
	...props
}: {
	meta: FieldMetadata<string | null | undefined>
	placeholder: string
	items: Array<{ value: string; label: string }>
	fieldErrors?: ListOfErrors
	className?: string
} & ComponentProps<typeof Select>) {
	const selectRef = useRef<ElementRef<typeof SelectTrigger>>(null)
	const control = useControl(meta)
	const fallbackId = useId()
	const id = meta.id ?? fallbackId
	const errorId = fieldErrors?.length ? `${id}-error` : undefined

	return (
		<div className={cn('relative', className)}>
			<select
				name={meta.name}
				defaultValue={meta.initialValue ?? ''}
				className="sr-only"
				ref={control.register}
				aria-hidden
				tabIndex={-1}
				onFocus={() => {
					selectRef.current?.focus()
				}}
			>
				<option value="" />
				{items.map((option) => (
					<option key={option.value} value={option.value} />
				))}
			</select>

			<Select
				{...props}
				value={control.value ?? ''}
				onValueChange={control.change}
				onOpenChange={(open) => {
					if (!open) {
						control.blur()
					}
				}}
			>
				<SelectTrigger
					className={cn(
						'h-14 border-b border-l-0 border-r-0 border-t-0 border-brand-dusk bg-transparent px-1 pb-4 font-serif text-2xl font-light md:text-3xl lg:text-4xl xl:pb-3 xl:text-5xl',
						!control.value && 'text-brand-dusk/85',
					)}
				>
					<SelectValue
						placeholder={placeholder ?? 'Selecciona una opción...'}
					/>
				</SelectTrigger>
				<SelectContent className={'rounded-none font-serif'}>
					{!meta.required && (
						<SelectItem
							value="-"
							className={'rounded-none text-lg lg:text-xl xl:text-2xl'}
						>
							{placeholder ?? 'Selecciona una opción...'}
						</SelectItem>
					)}
					{items.map((item) => {
						return (
							<SelectItem
								className={'rounded-none text-lg lg:text-xl xl:text-2xl'}
								key={item.value}
								value={item.value}
							>
								{item.label}
							</SelectItem>
						)
					})}
				</SelectContent>
			</Select>

			{errorId ? (
				<div className={'absolute -bottom-sm font-light'}>
					<ErrorList id={errorId} errors={fieldErrors} />
				</div>
			) : null}
		</div>
	)
}

// Same look and field wiring as PublicStyledSelect, but with a search box —
// meant for long lists (e.g. countries) where scrolling to find an option is
// impractical and native select type-ahead isn't discoverable enough.
export function PublicStyledComboboxField({
	meta,
	placeholder,
	items,
	fieldErrors,
	className,
	searchPlaceholder = 'Search...',
	emptyText = 'No results.',
}: {
	meta: FieldMetadata<string | null | undefined>
	placeholder: string
	items: Array<{ value: string; label: string }>
	fieldErrors?: ListOfErrors
	className?: string
	searchPlaceholder?: string
	emptyText?: string
}) {
	const control = useControl(meta)
	const fallbackId = useId()
	const id = meta.id ?? fallbackId
	const errorId = fieldErrors?.length ? `${id}-error` : undefined
	const [open, setOpen] = useState(false)

	const selectedLabel = items.find(
		(item) => item.value === control.value,
	)?.label

	return (
		<div className={cn('relative', className)}>
			<select
				name={meta.name}
				defaultValue={meta.initialValue ?? ''}
				className="sr-only"
				ref={control.register}
				aria-hidden
				tabIndex={-1}
			>
				<option value="" />
				{items.map((option) => (
					<option key={option.value} value={option.value} />
				))}
			</select>

			<Popover
				open={open}
				onOpenChange={(nextOpen) => {
					setOpen(nextOpen)
					if (!nextOpen) control.blur()
				}}
			>
				<PopoverTrigger asChild>
					<button
						type="button"
						id={id}
						className={cn(
							'flex h-14 w-full items-center justify-between gap-[2vw] border-b border-l-0 border-r-0 border-t-0 border-brand-dusk bg-transparent px-1 pb-4 font-serif text-2xl font-light focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-3xl lg:text-4xl xl:pb-3 xl:text-5xl',
							!control.value && 'text-brand-dusk/85',
						)}
					>
						<span className={'line-clamp-1 text-start'}>
							{selectedLabel ?? placeholder}
						</span>
						<Icon
							name={'chevrons-up-down'}
							size={'md'}
							className={'shrink-0 opacity-50'}
						/>
					</button>
				</PopoverTrigger>
				<PopoverContent
					align={'start'}
					// This field is reused both inline within a sentence (e.g. just
					// the word "país", a trigger too narrow to size the popover off
					// of) and as a full-width grid field elsewhere — so the popover
					// takes the larger of a readable minimum and the trigger's width.
					className={
						'w-[max(16rem,var(--radix-popover-trigger-width))] max-w-[90vw] rounded-none p-0 font-serif'
					}
				>
					<Command>
						<CommandInput placeholder={searchPlaceholder} />
						<CommandList>
							<CommandEmpty>{emptyText}</CommandEmpty>
							<CommandGroup>
								{items.map((item) => (
									<CommandItem
										key={item.value}
										value={item.label}
										className={'rounded-none text-lg lg:text-xl xl:text-2xl'}
										onSelect={() => {
											// Matches the workaround already used in
											// ComboboxField/PublicComboboxField: a single
											// call is sometimes swallowed by Conform's control.
											control.change(item.value)
											control.change(item.value)
											setOpen(false)
											control.blur()
										}}
									>
										<Icon
											name={'check'}
											className={cn(
												'mr-2 h-4 w-4 shrink-0',
												item.value === control.value
													? 'opacity-100'
													: 'opacity-0',
											)}
										/>
										{item.label}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			{errorId ? (
				<div className={'absolute -bottom-sm font-light'}>
					<ErrorList id={errorId} errors={fieldErrors} />
				</div>
			) : null}
		</div>
	)
}

export function PublicSelectWithLabel({
	meta,
	placeholder,
	label,
	items,
	fieldErrors,
	className,
	...props
}: {
	label?: string
	meta: FieldMetadata<string | null | undefined>
	placeholder: string
	items: Array<{ value: string; label: string }>
	fieldErrors?: ListOfErrors
	className?: string
} & ComponentProps<typeof Select>) {
	const selectRef = useRef<ElementRef<typeof SelectTrigger>>(null)
	const control = useControl(meta)
	const fallbackId = useId()
	const id = meta.id ?? fallbackId
	const errorId = fieldErrors?.length ? `${id}-error` : undefined

	return (
		<div className={cn('flex flex-col', className)}>
			<select
				name={meta.name}
				defaultValue={meta.initialValue ?? ''}
				className="sr-only"
				ref={control.register}
				aria-hidden
				tabIndex={-1}
				onFocus={() => {
					selectRef.current?.focus()
				}}
			>
				<option value="" />
				{items.map((option) => (
					<option key={option.value} value={option.value} />
				))}
			</select>

			{label && (
				<Label
					className={
						'text-body-2xs font-extralight uppercase tracking-widest xl:text-body-xs'
					}
				>
					{label}
				</Label>
			)}
			<Select
				{...props}
				value={control.value ?? ''}
				onValueChange={control.change}
				onOpenChange={(open) => {
					if (!open) {
						control.blur()
					}
				}}
			>
				<SelectTrigger
					className={cn(
						'h-14 border-b border-l-0 border-r-0 border-t-0 border-current bg-transparent px-0 py-4 text-body-2xs font-medium tracking-widest placeholder:uppercase xl:pb-3 xl:text-body-xs',
						!control.value && 'text-current/85',
					)}
				>
					<SelectValue
						placeholder={placeholder ?? 'Selecciona una opción...'}
					/>
				</SelectTrigger>
				<SelectContent className={'rounded-none'}>
					{!meta.required && (
						<SelectItem
							value="-"
							className={'rounded-none text-base lg:text-lg xl:text-xl'}
						>
							{placeholder ?? 'Selecciona una opción...'}
						</SelectItem>
					)}
					{items.map((item) => {
						return (
							<SelectItem
								className={'rounded-none text-base lg:text-lg xl:text-xl'}
								key={item.value}
								value={item.value}
							>
								{item.label}
							</SelectItem>
						)
					})}
				</SelectContent>
			</Select>

			{errorId ? (
				<div className="pt-2 font-light">
					<ErrorList id={errorId} errors={fieldErrors} />
				</div>
			) : null}
		</div>
	)
}

// Same look and field wiring as PublicSelectWithLabel, but with a search box —
// meant for long lists (e.g. countries) where scrolling to find an option is
// impractical and native select type-ahead isn't discoverable enough.
export function PublicComboboxField({
	meta,
	placeholder,
	label,
	items,
	fieldErrors,
	className,
	searchPlaceholder = 'Search...',
	emptyText = 'No results.',
}: {
	label?: string
	meta: FieldMetadata<string | null | undefined>
	placeholder: string
	items: Array<{ value: string; label: string }>
	fieldErrors?: ListOfErrors
	className?: string
	searchPlaceholder?: string
	emptyText?: string
}) {
	const control = useControl(meta)
	const fallbackId = useId()
	const id = meta.id ?? fallbackId
	const errorId = fieldErrors?.length ? `${id}-error` : undefined
	const [open, setOpen] = useState(false)

	const selectedLabel = items.find(
		(item) => item.value === control.value,
	)?.label

	return (
		<div className={cn('flex flex-col', className)}>
			<select
				name={meta.name}
				defaultValue={meta.initialValue ?? ''}
				className="sr-only"
				ref={control.register}
				aria-hidden
				tabIndex={-1}
			>
				<option value="" />
				{items.map((option) => (
					<option key={option.value} value={option.value} />
				))}
			</select>

			{label && (
				<Label
					className={
						'text-body-2xs font-extralight uppercase tracking-widest xl:text-body-xs'
					}
				>
					{label}
				</Label>
			)}

			<Popover
				open={open}
				onOpenChange={(nextOpen) => {
					setOpen(nextOpen)
					if (!nextOpen) control.blur()
				}}
			>
				<PopoverTrigger asChild>
					<button
						type="button"
						id={id}
						className={cn(
							'flex h-14 items-center justify-between gap-sm border-b border-current bg-transparent px-0 py-4 text-start text-body-2xs font-medium tracking-widest xl:pb-3 xl:text-body-xs',
							!control.value && 'uppercase text-current/85',
						)}
					>
						<span className={'line-clamp-1'}>
							{selectedLabel ?? placeholder}
						</span>
						<Icon
							name={'chevrons-up-down'}
							size={'md'}
							className={'opacity-50'}
						/>
					</button>
				</PopoverTrigger>
				<PopoverContent
					align={'start'}
					className={'w-[var(--radix-popover-trigger-width)] rounded-none p-0'}
				>
					<Command>
						<CommandInput placeholder={searchPlaceholder} />
						<CommandList>
							<CommandEmpty>{emptyText}</CommandEmpty>
							<CommandGroup>
								{items.map((item) => (
									<CommandItem
										key={item.value}
										value={item.label}
										onSelect={() => {
											// Matches the workaround already used in
											// ComboboxField below: a single call is
											// sometimes swallowed by Conform's control.
											control.change(item.value)
											control.change(item.value)
											setOpen(false)
											control.blur()
										}}
									>
										<Icon
											name={'check'}
											className={cn(
												'mr-2 h-4 w-4',
												item.value === control.value
													? 'opacity-100'
													: 'opacity-0',
											)}
										/>
										{item.label}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			{errorId ? (
				<div className="pt-2 font-light">
					<ErrorList id={errorId} errors={fieldErrors} />
				</div>
			) : null}
		</div>
	)
}

export function ComboboxField({
	triggerProps,
	labelProps,
	meta,
	placeholder = 'Escoge uno...',
	className = '',
	fieldErrors,
	items,
}: {
	triggerProps?: JSX.IntrinsicElements['button']
	labelProps: JSX.IntrinsicElements['label']
	meta: FieldMetadata<string | null | undefined>
	className?: string
	placeholder?: string
	items: Array<{ value: string; label: string }>
	fieldErrors?: ListOfErrors
}) {
	const selectRef = useRef<ElementRef<typeof SelectTrigger>>(null)
	const control = useControl(meta)
	const fallbackId = useId()
	const id = meta.id ?? fallbackId
	const errorId = fieldErrors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} className="text-muted-foreground" {...labelProps}>
				{labelProps.children}
				{meta.required && <span className="text-xs text-red-600"> *</span>}
			</Label>
			<select
				id={id}
				name={meta.name}
				defaultValue={meta.initialValue ?? ''}
				className="sr-only"
				ref={control.register}
				aria-hidden
				tabIndex={-1}
				onFocus={() => {
					selectRef.current?.focus()
				}}
			>
				<option value="" />
				{items.map((option) => (
					<option key={option.value} value={option.value} />
				))}
			</select>
			<Popover>
				<PopoverTrigger asChild>
					<Button
						{...triggerProps}
						size="sm"
						variant="outline"
						className={cn(
							'flex h-10 w-full items-center justify-between gap-[2vw] border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 [&>span]:text-start',
							triggerProps?.className,
						)}
					>
						{control.value || meta.value
							? items.find(
									(item) => item.value === (control.value || meta.value),
								)?.label
							: placeholder}
					</Button>
				</PopoverTrigger>
				<PopoverContent align="start">
					<Command>
						<CommandInput placeholder="Busca un elemento" />
						<CommandList>
							<CommandEmpty>No hay elementos.</CommandEmpty>
							<CommandGroup>
								{items.map((item) => (
									<CommandItem
										key={item.value}
										value={item.value}
										onSelect={() => {
											// This is a bug... if I only enter it once, the value does not change... To avoid having the user need to click twice, I do it for them.
											control.change(item.value)
											control.change(item.value)
										}}
									>
										<Icon
											name={'check'}
											className={cn(
												'mr-2 h-4 w-4',
												item.value === control.value
													? 'opacity-100'
													: 'opacity-0',
											)}
										/>
										{item.label}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={fieldErrors} />
				</div>
			) : null}
		</div>
	)
}

export function SelectField({
	labelProps,
	meta,
	placeholder,
	fieldErrors,
	items,
	className,
	...props
}: {
	labelProps: JSX.IntrinsicElements['label']
	meta: FieldMetadata<string | null | undefined>
	placeholder?: string
	items: Array<{ value: string; label: string }>
	fieldErrors?: ListOfErrors
	className?: string
} & ComponentProps<typeof Select>) {
	const selectRef = useRef<ElementRef<typeof SelectTrigger>>(null)
	const control = useControl(meta)
	const fallbackId = useId()
	const id = meta.id ?? fallbackId
	const errorId = fieldErrors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} className="text-muted-foreground" {...labelProps}>
				{labelProps.children}
				{meta.required && <span className="text-xs text-red-600"> *</span>}
			</Label>
			<select
				name={meta.name}
				defaultValue={meta.initialValue ?? ''}
				className="sr-only"
				ref={control.register}
				aria-hidden
				tabIndex={-1}
				onFocus={() => {
					selectRef.current?.focus()
				}}
			>
				<option value="" />
				{items.map((option) => (
					<option key={option.value} value={option.value} />
				))}
			</select>
			<Select
				{...props}
				value={control.value ?? ''}
				onValueChange={control.change}
				onOpenChange={(open) => {
					if (!open) {
						control.blur()
					}
				}}
			>
				<SelectTrigger>
					<SelectValue
						placeholder={placeholder ?? 'Selecciona una opción...'}
					/>
				</SelectTrigger>
				<SelectContent>
					{!meta.required && (
						<SelectItem value="-">
							{placeholder ?? 'Selecciona una opción...'}
						</SelectItem>
					)}
					{items.map((item) => {
						return (
							<SelectItem key={item.value} value={item.value}>
								{item.label}
							</SelectItem>
						)
					})}
				</SelectContent>
			</Select>
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={fieldErrors} />
				</div>
			) : null}
		</div>
	)
}

export function RichTextField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
		id?: string
		name: string
		form: string
		value?: string
		placeholder?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const input = useInputControl({
		name: textareaProps.name,
		formId: textareaProps.form,
		initialValue: textareaProps.defaultValue
			? String(textareaProps.defaultValue)
			: undefined,
	})
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<Label htmlFor={id} className="text-muted-foreground" {...labelProps} />
			<RichTextInput
				options={{
					content: input.value,
					onUpdate: (p) => input.change(p.editor.getHTML()),
					onFocus: () => input.focus(),
					onBlur: () => input.blur(),
				}}
				placeholder={textareaProps.placeholder}
			/>
			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function PublicFileUploadField({
	labelProps,
	files,
	setFiles,
	errors,
	className,
	required,
	dropZoneConfig,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	files: File[] | null
	setFiles: (files: File[] | null) => void
	dropZoneConfig?: DropzoneOptions
	errors?: ListOfErrors
	className?: string
	required?: boolean
}) {
	const { t } = useTranslation('common')
	const id = useId()
	const errorId = errors?.length ? `${id}-error` : undefined

	const config = {
		maxFiles: dropZoneConfig?.maxFiles ?? 30,
		maxSize: IMAGES,
		multiple: dropZoneConfig?.multiple ?? true,
		accept: dropZoneConfig?.accept ?? {
			'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
		},
		...dropZoneConfig,
	}

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<Label
				htmlFor={id}
				{...labelProps}
				className={
					'text-body-2xs font-medium uppercase tracking-widest xl:text-body-xs'
				}
			>
				{labelProps.children}{' '}
				{files && files.length > 0 ? `(${files.length})` : ''}
				{required && <span>*</span>}
			</Label>

			<FileUploader
				value={files}
				onValueChange={setFiles}
				dropzoneOptions={config}
				className="relative overflow-hidden rounded-none bg-transparent"
			>
				<FileInput
					className={
						'flex items-center gap-sm rounded-none border-b border-brand-dusk pb-4 pt-2 text-body-2xs font-extralight uppercase tracking-widest xl:text-body-xs'
					}
				>
					<LucideCirclePlus
						className={'size-7 stroke-1 group-disabled:opacity-30'}
					/>
					<Trans
						t={t}
						i18nKey={'common:form.fields.files.selectFile'}
						count={config.maxFiles}
					/>
				</FileInput>

				<FileUploaderContent>
					{files &&
						files.length > 0 &&
						files.map((file, i) => {
							return (
								<FileUploaderItem key={i} index={i}>
									<Icon
										name="lucide-paperclip"
										className="h-4 w-4 stroke-current"
									/>
									{file.type.startsWith('image') ? (
										<img
											className="h-full max-w-[25%] rounded"
											src={URL.createObjectURL(file) ?? ''}
											alt={file.name}
										/>
									) : (
										<Icon
											name="file-check-2"
											className="h-4 w-4 stroke-current"
										/>
									)}
									<span className="truncate">{file.name}</span>
								</FileUploaderItem>
							)
						})}
				</FileUploaderContent>
			</FileUploader>

			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function FileUploadField({
	labelProps,
	files,
	setFiles,
	errors,
	dropZoneConfig,
	className,
	required,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	files: File[] | null
	setFiles: (files: File[] | null) => void
	errors?: ListOfErrors
	dropZoneConfig?: DropzoneOptions
	className?: string
	required?: boolean
}) {
	const id = useId()
	const errorId = errors?.length ? `${id}-error` : undefined

	const config = {
		maxFiles: dropZoneConfig?.maxFiles ?? 30,
		maxSize: IMAGES,
		multiple: dropZoneConfig?.multiple ?? true,
		accept: dropZoneConfig?.accept ?? {
			'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
		},
		...dropZoneConfig,
	}

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<Label htmlFor={id} className="text-muted-foreground" {...labelProps}>
				{labelProps.children}{' '}
				{files && files.length > 0 ? `(${files.length})` : ''}
				{required && <span className="text-xs text-red-600">*</span>}
			</Label>

			<FileUploader
				value={files}
				onValueChange={setFiles}
				dropzoneOptions={config}
				className="relative overflow-hidden rounded-lg bg-background"
			>
				<FileInput className="relative border-2 border-dashed border-muted">
					<div className="flex max-h-[140px] w-full flex-col items-center justify-center overflow-hidden px-xs pb-3 pt-3 text-center">
						<FileSvgDraw maxSize={config.maxSize} />
					</div>
				</FileInput>
				<FileUploaderContent>
					{files &&
						files.length > 0 &&
						files.map((file, i) => {
							return (
								<FileUploaderItem key={i} index={i}>
									<Icon
										name="lucide-paperclip"
										className="h-4 w-4 stroke-current"
									/>
									{file.type.startsWith('image') ? (
										<img
											className="h-full max-w-[25%] rounded"
											src={URL.createObjectURL(file) ?? ''}
											alt={file.name}
										/>
									) : (
										<Icon
											name="file-check-2"
											className="h-4 w-4 stroke-current"
										/>
									)}
									<span className="truncate">{file.name}</span>
								</FileUploaderItem>
							)
						})}
				</FileUploaderContent>
			</FileUploader>

			{errorId ? (
				<div className="pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

const FileSvgDraw = ({ maxSize }: { maxSize: number }) => {
	return (
		<>
			<Icon
				name="lucide-image-up"
				size="lg"
				className="mb-xxs text-brand-dusk/80"
			/>
			<p className="mb-1 text-sm text-gray-500">
				<span className="font-semibold">
					Haz click para seleccionar imágenes,
				</span>
				&nbsp; o arrástra y suélta aquí.
			</p>
			<p className="mb-xxs text-xs text-gray-500">SVG, PNG, JPG or GIF</p>
			<Icon name="lucide-info" size="xs" className="text-gray-700">
				<p className="text-xs text-gray-700">
					{maxSize / 1024 / 1024}MB max. por archivo
				</p>
			</Icon>
		</>
	)
}
